/**
 * Modal history-back — "Android back button closes the modal instead of leaving
 * the page". On show it pushes a history entry; a back-press pops it and closes
 * the modal; closing the modal by other means pops the entry back off so the URL
 * stays clean.
 *
 * This is the **Bootstrap-bound driver** over the engine-agnostic
 * `setupHistoryBackToClose` state machine (overlay-close-driver.ts): it
 * translates the engine's `show`/`hidden` lifecycle into the primitive's
 * `open()`/`closed()` signals and supplies `engine.close(modal)` as the close
 * action. The same primitive drives the parent product-image lightbox via
 * `gallery-lightbox-adapter.ts` — one back-to-close state machine, two drivers
 * (the divergence ADR-0007/0008 set out to avoid).
 *
 * Engine discipline (DIP): this module NEVER probes jQuery / window.bootstrap /
 * eModal itself — it routes both the close call and the show/hidden lifecycle
 * subscriptions through the one `ModalEngine` seam (modal-engine.ts), the single
 * file allowed to know which engine is live.
 *
 * It self-manages its own teardown via the modal's own `hidden` event rather than
 * returning an `Unbind`, so the controller wires it fire-and-forget.
 *
 * Sibling-overlay hand-off seam (ADR-0008 Decision-History — the #5 → mini-cart
 * case): a feature whose success path opens a SIBLING overlay (e.g. the IPL
 * add-to-cart in kategorie.ts opens the mini-cart, which pushes its OWN history
 * entry) must let THIS modal surrender its back-entry FIRST, while the stack is
 * still clean. It does that by dispatching the `datte:modal-release-history`
 * CustomEvent on the modal element BEFORE opening the sibling; we translate it
 * into `backToClose.release(onSettled)`. This keeps the feature code decoupled
 * from the history-back internals — it only knows the documented event contract,
 * not the driver/primitive (DIP). The event is generic: ANY overlay-before-sibling
 * case can reuse it, no per-modal wiring.
 *
 * @see docs/architecture/modals/target-architecture.md §3.2.3
 * @see docs/architecture/modals/shop-foundations.md §4.5 (engine-probe discipline, G5/G10)
 */

import { modalEngine, type ModalEngine } from './modal-engine';
import { setupHistoryBackToClose } from './overlay-close-driver';

/**
 * Event a feature dispatches on a modal element to make it surrender its
 * history-back entry BEFORE the feature opens a sibling overlay. `detail.onSettled`
 * (optional) runs once the entry has actually been popped — the feature opens the
 * sibling there, guaranteeing a clean stack. Use {@link dispatchReleaseHistory}
 * rather than constructing the event by hand.
 */
export const MODAL_RELEASE_HISTORY_EVENT = 'datte:modal-release-history';

export interface ModalReleaseHistoryDetail {
  /** Run once this modal's history entry has been popped (the stack is clean). */
  onSettled?: () => void;
}

/**
 * Ask the modal `el` to release its history-back entry, then run `onSettled` once
 * the stack is clean. Type-safe wrapper over the `datte:modal-release-history`
 * CustomEvent so call sites need no knowledge of the history-back internals.
 *
 * If the modal has no history-back wired (no listener for the event), `onSettled`
 * still runs synchronously via the fallback below, so the feature path is safe
 * whether or not `historyBack` is enabled on the shell — fail-safe, not fail-loud:
 * a modal without history-back simply has nothing to release.
 */
export function dispatchReleaseHistory(el: HTMLElement, onSettled?: () => void): void {
    let handled = false;
    const detail: ModalReleaseHistoryDetail = {
        onSettled: () => {
            handled = true;
            onSettled?.();
        },
    };
    el.dispatchEvent(new CustomEvent<ModalReleaseHistoryDetail>(MODAL_RELEASE_HISTORY_EVENT, { detail }));
    // No history-back listener consumed the event (the wrapped onSettled never
    // ran) → nothing to release; run the continuation now so the caller proceeds.
    if (!handled) onSettled?.();
}

export function setupModalHistoryBack(modal: HTMLElement, engine: ModalEngine = modalEngine): void {
    // The engine-agnostic state machine; close = the engine's hide. The `datteModal`
    // tag keeps the pushed entry attributable to this modal (back-compat with the
    // prior inline implementation + its tests).
    const backToClose = setupHistoryBackToClose(() => engine.close(modal), {
        datteModal: modal.id,
        datteModalBack: true,
    });

    // Push on `show` (not `shown`): show.bs.modal fires synchronously on the trigger
    // click, before the 300 ms fade — pushing here closes the race window where a
    // quick Android back-swipe mid-animation would navigate away instead of closing.
    engine.onShow(modal, () => backToClose.open());
    engine.onHidden(modal, () => backToClose.closed());

    // Sibling-overlay hand-off (see module header): release this modal's entry on
    // demand, BEFORE the feature opens a sibling overlay, then settle the caller.
    modal.addEventListener(MODAL_RELEASE_HISTORY_EVENT, (e) => {
        const detail = (e as CustomEvent<ModalReleaseHistoryDetail>).detail;
        backToClose.release(detail?.onSettled);
    });
}
